import {
  Button,
  Col,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IHomeHeroCardsService } from "@shared/interfaces/home-hero-cards-service.interface";
import { HomeHeroCardStatus } from "@shared/types/home-hero-card/enums/home-hero-card-status.enum";
import { HomeHeroCardDto } from "@shared/types/home-hero-card/home-hero-card.dto";
import { ColumnsType } from "antd/es/table";
import { useMutation, useQuery } from "react-query";
import AlertModal from "../Popups/AlertModal/AlertModal";
import ChangeHomeHeroCardModal from "./ChangeHomeHeroCardModal/ChangeHomeHeroCardModal";

const ALL = "All";
const STATUSES = [
  { label: "All", value: ALL },
  { label: "Active", value: HomeHeroCardStatus.ACTIVE },
  { label: "Inactive", value: HomeHeroCardStatus.INACTIVE },
];

const HomeHeroCards = () => {
  const [status, setStatus] = useState(ALL);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<HomeHeroCardDto | null>(
    null,
  );

  const homeHeroCardsService = appInject<IHomeHeroCardsService>(
    DI_TOKENS.homeHeroCardsService,
  );

  const columns = useMemo(
    () =>
      [
        {
          key: "slug",
          dataIndex: "slug",
          title: "Slug",
          width: 180,
        },
        {
          key: "imageUrl",
          dataIndex: "imageUrl",
          title: "Image",
          width: 80,
          render: (imageUrl: string | null) =>
            imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                style={{ width: 48, height: 48, objectFit: "contain" }}
              />
            ) : null,
        },
        {
          key: "status",
          dataIndex: "status",
          title: "Status",
          width: 100,
          render: (cardStatus: HomeHeroCardStatus) => (
            <Tag
              color={
                cardStatus === HomeHeroCardStatus.ACTIVE ? "#009E60" : "#f5222d"
              }
            >
              {cardStatus === HomeHeroCardStatus.ACTIVE ? "Active" : "Inactive"}
            </Tag>
          ),
        },
        {
          key: "sortOrder",
          dataIndex: "sortOrder",
          title: "Sort",
          width: 80,
        },
        {
          key: "activeFrom",
          dataIndex: "activeFrom",
          title: "Active From",
          width: 200,
          render: (date: string | null) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
        },
        {
          key: "activeTo",
          dataIndex: "activeTo",
          title: "Active To",
          width: 200,
          render: (date: string | null) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
        },
        {
          key: "action",
          title: "Actions",
          width: 180,
          render: (_: null, heroCard: HomeHeroCardDto) => (
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setSelectedCard(heroCard);
                  setIsOpenModal(true);
                }}
              >
                Edit
              </Button>
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => {
                  setSelectedCard(heroCard);
                  setIsAlertModalOpen(true);
                }}
              >
                Delete
              </Button>
            </Space>
          ),
        },
      ] as ColumnsType<HomeHeroCardDto>,
    [],
  );

  const cardsQuery = useQuery(
    ["home-hero-cards-list"],
    () => homeHeroCardsService.list(),
    { refetchOnMount: true },
  );

  const filteredCards = useMemo(() => {
    const cards = cardsQuery.data ?? [];
    const search = debouncedSearchText.trim().toLowerCase();
    return cards.filter((heroCard) => {
      const matchesStatus = status === ALL || heroCard.status === status;
      const matchesSearch =
        !search || heroCard.slug.toLowerCase().includes(search);
      return matchesStatus && matchesSearch;
    });
  }, [cardsQuery.data, debouncedSearchText, status]);

  const pagedCards = useMemo(() => {
    const start = (page - 1) * currentPageSize;
    return filteredCards.slice(start, start + currentPageSize);
  }, [filteredCards, page, currentPageSize]);

  const deleteMutation = useMutation(
    (id: string) => homeHeroCardsService.delete(id),
    {
      onSuccess: () => {
        setIsAlertModalOpen(false);
        setSelectedCard(null);
        cardsQuery.refetch();
      },
    },
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [status]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);
  }, []);

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  return (
    <Layout style={{ height: "100%", padding: "20px" }}>
      {isOpenModal && (
        <ChangeHomeHeroCardModal
          open={isOpenModal}
          card={selectedCard}
          onClose={() => {
            setIsOpenModal(false);
            setSelectedCard(null);
          }}
          onSuccess={() => {
            setIsOpenModal(false);
            setSelectedCard(null);
            cardsQuery.refetch();
          }}
        />
      )}

      {isAlertModalOpen && (
        <AlertModal
          open={isAlertModalOpen}
          description={`Are you sure you want to delete "${selectedCard?.slug}"?`}
          onClose={() => setIsAlertModalOpen(false)}
          onProceed={() =>
            selectedCard?.id && deleteMutation.mutate(selectedCard.id)
          }
          isLoading={deleteMutation.isLoading}
          error={deleteMutation.error ? "Failed to delete" : undefined}
        />
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={4}>
            Home Hero Cards ({filteredCards.length})
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search by slug"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              options={STATUSES}
              value={status}
              onChange={setStatus}
              style={{ width: 120 }}
            />
            <Button
              type="primary"
              onClick={() => {
                setSelectedCard(null);
                setIsOpenModal(true);
              }}
            >
              Add Card
            </Button>
          </Space>
        </Col>
      </Row>

      <Layout.Content
        ref={containerRef}
        style={{ flex: 1, backgroundColor: "white" }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={pagedCards}
          loading={cardsQuery.isFetching}
          pagination={{
            current: page,
            total: filteredCards.length,
            pageSize: currentPageSize,
            onChange: (nextPage, pageSize) =>
              onTableChange({ current: nextPage, pageSize }),
          }}
          scroll={{ y: containerHeight }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default HomeHeroCards;
